import type { BaseEntityDto, Id, IsoDate } from './common.js';

/** Tools do v0.1 (secao 8.5). No Sprint 1 apenas as tres primeiras estao ativas. */
/**
 * R028 - PARA QUE uma chamada de IA serviu.
 *
 * Sem esta separacao, atender um cliente e responder uma pergunta do dono somam
 * no mesmo balde, e nao da para dizer onde o dinheiro foi - nem para cobrar
 * diferente por coisas que custam diferente.
 */
export const AI_FEATURES = ['atendimento', 'gestao', 'simulador', 'avaliacao'] as const;
export type AIFeature = (typeof AI_FEATURES)[number];

/**
 * VZ1 - funções que aparecem no LIVRO de consumo, além das que escolhem modelo
 * de texto. `voz` (transcrição do microfone) é medida em minutos, não em
 * tokens, e não tem modelo de texto a escolher na plataforma — por isso fica
 * fora de `AI_FEATURES` (que alimenta a escolha de modelo por função).
 */
export const AI_USAGE_FEATURES = [...AI_FEATURES, 'voz'] as const;
export type AIUsageFeature = (typeof AI_USAGE_FEATURES)[number];

export const AI_FEATURE_LABELS: Record<AIFeature, string> = {
  atendimento: 'Atendimento ao cliente',
  gestao: 'Conversa de gestao',
  simulador: 'Simulador',
  avaliacao: 'Avaliacao de qualidade',
};

export const TOOL_NAMES = [
  'prepareCommerceOrder',
  'searchCommerceCatalog',
  'searchBusinessCatalog',
  'trackCommerceOrder',
  'quoteCommerceDelivery',
  'reserveCatalogItem',
  'listMyStockReservations',
  'updateCustomerProfile',
  'ensureOpportunity',
  'moveOpportunityStage',
  'searchKnowledge',
  'getAvailableSlots',
  'previewAppointmentSeries',
  'createAppointment',
  'joinAppointmentWaitlist',
  'checkHealthCoverage',
  'checkMyHealthAuthorization',
  'checkMyCarePackage',
  'requestMyCarePackageRefund',
  'requestMyLessonReplacement',
  'reportWrongRecipient',
  'classifyUnrelatedConversation',
  'scheduleFollowUp',
  'recordCustomerFeedback',
  'registerMarketingOptOut',
  'requestPersonalDataReview',
  'requestAfterSalesReview',
  'requestHumanHandoff',
] as const;
export type ToolName = (typeof TOOL_NAMES)[number];

/**
 * O que a assistente esta fazendo AGORA. Gerundio de proposito: aparece ao vivo
 * enquanto a acao acontece ("Atualizando contato...").
 *
 * Jargao tecnico nunca vaza (Apendice H.2).
 */
export const TOOL_LABELS: Record<ToolName, string> = {
  prepareCommerceOrder: 'Preparando pedido',
  searchCommerceCatalog: 'Consultando produtos',
  searchBusinessCatalog: 'Consultando preço e disponibilidade',
  trackCommerceOrder: 'Consultando pedido',
  quoteCommerceDelivery: 'Calculando entrega',
  reserveCatalogItem: 'Reservando produto',
  listMyStockReservations: 'Consultando reservas',
  updateCustomerProfile: 'Atualizando contato',
  ensureOpportunity: 'Registrando oportunidade',
  moveOpportunityStage: 'Atualizando etapa',
  searchKnowledge: 'Consultando informações da empresa',
  getAvailableSlots: 'Consultando agenda',
  previewAppointmentSeries: 'Conferindo série de atendimentos',
  createAppointment: 'Agendando',
  joinAppointmentWaitlist: 'Incluindo na lista de espera',
  checkHealthCoverage: 'Consultando convênios',
  checkMyHealthAuthorization: 'Consultando autorização',
  checkMyCarePackage: 'Consultando pacote',
  requestMyCarePackageRefund: 'Registrando pedido de devolução',
  requestMyLessonReplacement: 'Pedindo reposição de aula',
  reportWrongRecipient: 'Interrompendo mensagens para número incorreto',
  classifyUnrelatedConversation: 'Classificando contato sem relação',
  scheduleFollowUp: 'Programando acompanhamento',
  recordCustomerFeedback: 'Registrando retorno do cliente',
  registerMarketingOptOut: 'Interrompendo promoções',
  requestPersonalDataReview: 'Abrindo solicitação de dados',
  requestAfterSalesReview: 'Abrindo protocolo de pós-venda',
  requestHumanHandoff: 'Chamando a equipe',
};

/**
 * RIM-188 - o que a assistente SABE FAZER (secao 11.7).
 *
 * A mesma ferramenta precisa de duas frases porque responde a duas perguntas
 * diferentes. "Atualizando contato" e o que esta acontecendo neste instante;
 * numa lista de capacidades, o gerundio fica errado — ninguem descreve o que um
 * funcionario sabe fazer dizendo "atualizando clientes".
 *
 * Os textos sao os que a secao 11.7 nomeia, e falam de CLIENTE e CONSULTA, nao
 * de "contato" e "agendamento": e a lingua de quem trabalha na recepcao.
 */
export const TOOL_CAPABILITIES: Record<ToolName, string> = {
  prepareCommerceOrder: 'Preparar pedido sem confirmar pagamento',
  searchCommerceCatalog: 'Consultar catálogo comercial',
  searchBusinessCatalog: 'Consultar produtos da empresa (sem custos)',
  trackCommerceOrder: 'Consultar pedido com código de acompanhamento',
  quoteCommerceDelivery: 'Calcular cobertura, frete e prazo de entrega',
  reserveCatalogItem: 'Reservar produto para o cliente',
  listMyStockReservations: 'Consultar reservas do próprio cliente',
  updateCustomerProfile: 'Atualizar clientes',
  ensureOpportunity: 'Criar oportunidades',
  moveOpportunityStage: 'Acompanhar o andamento',
  searchKnowledge: 'Responder sobre a empresa',
  getAvailableSlots: 'Consultar horários',
  previewAppointmentSeries: 'Conferir série de atendimentos antes de marcar',
  createAppointment: 'Agendar atendimentos',
  joinAppointmentWaitlist: 'Incluir na lista de espera',
  checkHealthCoverage: 'Consultar serviços cobertos por convênios',
  checkMyHealthAuthorization: 'Consultar saldo de autorização após validar identidade',
  checkMyCarePackage: 'Consultar saldo e devolução de pacote após validar identidade',
  requestMyCarePackageRefund: 'Solicitar análise de devolução de pacote',
  requestMyLessonReplacement: 'Pedir reposição de aula com crédito de falta avisada',
  reportWrongRecipient: 'Interromper mensagens enviadas ao número incorreto',
  classifyUnrelatedConversation: 'Classificar conversa destinada a outro estabelecimento',
  scheduleFollowUp: 'Acompanhar clientes',
  recordCustomerFeedback: 'Registrar elogios e sugestões',
  registerMarketingOptOut: 'Interromper promoções a pedido do cliente',
  requestPersonalDataReview: 'Protocolar pedido sobre dados pessoais',
  requestAfterSalesReview: 'Registrar pedido de ajuda com uma compra',
  requestHumanHandoff: 'Chamar a equipe',
};

/** Politica de preco do template Dental (secao 9.5). */
export const PRICING_POLICIES = ['EXACT', 'FROM', 'EVALUATION_REQUIRED'] as const;
export type PricingPolicy = (typeof PRICING_POLICIES)[number];

export const AGENT_MEDIA_LANGUAGES = ['pt-BR', 'es', 'en'] as const;
export type AgentMediaLanguage = (typeof AGENT_MEDIA_LANGUAGES)[number];
export const AGENT_MEDIA_FAILURE_ACTIONS = ['ask_text', 'human'] as const;
export type AgentMediaFailureAction = (typeof AGENT_MEDIA_FAILURE_ACTIONS)[number];
export const AGENT_REPLY_MODES = ['text', 'voice_and_text'] as const;
export type AgentReplyMode = (typeof AGENT_REPLY_MODES)[number];
export const AGENT_AUDIO_MIME_TYPES = ['audio/ogg', 'audio/opus', 'audio/mpeg'] as const;
export type AgentAudioMimeType = (typeof AGENT_AUDIO_MIME_TYPES)[number];
export const AGENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
export type AgentImageMimeType = (typeof AGENT_IMAGE_MIME_TYPES)[number];
export interface AgentMediaSettings {
  acceptAudio: boolean;
  acceptImages: boolean;
  extractImageText: boolean;
  maxAudioSeconds: number;
  maxAudioBytes: number;
  maxImageBytes: number;
  maxImagesPerRequest: number;
  audioMimeTypes: AgentAudioMimeType[];
  imageMimeTypes: AgentImageMimeType[];
  expectedLanguage: AgentMediaLanguage;
  onFailure: AgentMediaFailureAction;
  replyMode: AgentReplyMode;
  /** Voice never replaces the readable and searchable answer. */
  alwaysIncludeTextSummary: true;
}
export const DEFAULT_AGENT_MEDIA_SETTINGS: AgentMediaSettings = {
  acceptAudio: true,
  acceptImages: true,
  extractImageText: true,
  maxAudioSeconds: 180,
  maxAudioBytes: 10 * 1024 * 1024,
  maxImageBytes: 5 * 1024 * 1024,
  maxImagesPerRequest: 5,
  audioMimeTypes: [...AGENT_AUDIO_MIME_TYPES],
  imageMimeTypes: [...AGENT_IMAGE_MIME_TYPES],
  expectedLanguage: 'pt-BR',
  onFailure: 'ask_text',
  replyMode: 'text',
  alwaysIncludeTextSummary: true,
};

export interface AgentDto extends BaseEntityDto {
  localOnly?: boolean;
  commerceLimits?: { maxUnits: number; maxTotalCents: number } | null;
  companyId: Id;
  name: string;
  role: string;
  objective: string;
  personality: string;
  instructions: string;
  isActive: boolean;
  enabledTools: ToolName[];
  mediaSettings: AgentMediaSettings;
  pricingPolicy: PricingPolicy;
  /** Versao do prompt em uso, para regressao (RIM-127). */
  promptVersion: string;
}

/**
 * RIM-188 - o que a assistente fez HOJE (secao 11.7).
 *
 * "Hoje" e o dia da clinica, no fuso dela. Contar de meia-noite UTC daria
 * numeros errados no Brasil inteiro depois das 21h — a tela mostraria zero
 * enquanto a recepcao ainda estivesse trabalhando.
 */
export interface AgentTodayDto {
  conversations: number;
  opportunities: number;
  appointments: number;
}

export interface UpdateAgentRequest {
  commerceLimits?: { maxUnits: number; maxTotalCents: number } | null;
  name?: string;
  role?: string;
  objective?: string;
  personality?: string;
  instructions?: string;
  isActive?: boolean;
  pricingPolicy?: PricingPolicy;
  mediaSettings?: AgentMediaSettings;
}

/**
 * Acoes que a empresa NAO pode desligar.
 * Sem `requestHumanHandoff`, um cliente com urgencia nao chega a uma pessoa.
 */
export const REQUIRED_TOOL_NAMES: readonly ToolName[] = ['requestHumanHandoff'];

export interface UpdateAgentToolsRequest {
  enabledTools: ToolName[];
}

/** RIM-150 - Agent Playground: testar sem falar com cliente e sem gravar nada. */
export interface PlaygroundTurnDto {
  role: 'user' | 'assistant';
  text: string;
}

export interface PlaygroundActionDto {
  name: ToolName;
  /** Rotulo de negocio, o mesmo que a equipe ve no Inbox. */
  label: string;
  arguments: Record<string, unknown>;
  /** true quando a acao rodou de verdade (somente leitura). */
  executed: boolean;
  summary?: string;
}

export interface PlaygroundResultDto {
  memory: PlaygroundMemory;
  reply: string;
  actions: PlaygroundActionDto[];
  model: string;
  promptVersion: string;
  inputTokens: number;
  outputTokens: number;
}

export interface RunPlaygroundRequest {
  memory?: PlaygroundMemory;
  turns: PlaygroundTurnDto[];
  customerName?: string;
}

/** Estado fictício do teste; nunca é utilizado como identificador de um registro real. */
export interface PlaygroundMemory {
  fields: Record<string, string>;
  summary: string | null;
  interest: string | null;
  stageKey: string | null;
  booking: { service: string; when: string } | null;
}

export const AI_EXECUTION_STATUSES = ['running', 'completed', 'failed'] as const;
export type AIExecutionStatus = (typeof AI_EXECUTION_STATUSES)[number];

export interface AIToolCallTrace {
  name: string;
  ok: boolean;
  durationMs: number;
  /** Resumo do resultado; nunca o raciocinio privado do modelo. */
  resultSummary?: string | null;
  errorMessage?: string | null;
}

export interface AIExecutionDto extends BaseEntityDto {
  companyId: Id;
  conversationId: Id;
  contactId: Id;
  agentId: Id;
  status: AIExecutionStatus;
  provider: string;
  model: string;
  promptVersion: string;
  inboundMessageIds: Id[];
  outboundMessageIds: Id[];
  toolCalls: AIToolCallTrace[];
  loops: number;
  inputTokens?: number | null;
  outputTokens?: number | null;
  costUsd?: number | null;
  latencyMs?: number | null;
  errorMessage?: string | null;
  startedAt: IsoDate;
  finishedAt?: IsoDate | null;
}
